export interface ChatSession {
    sessionId: string;
    conversationId: string;
    createdAt: string;
}

export interface ChatMessage {
    id: string;
    text: string;
    isUser: boolean;
    timestamp: string;
    requestId?: string;
}

export interface SendChatResponse {
    message: ChatMessage;
    aiResponse: ChatMessage;
}

interface PendingRequest {
    message: string;
    status: 'sending' | 'processing' | 'streaming' | 'completed' | 'failed';
    timestamp: number;
    retryCount: number;
    timeout?: NodeJS.Timeout;
}

// API Configuration
const API_BASE_URL = process.env.REACT_APP_IDWAL_API_URL || "http://localhost";

class AskIdwalAIService {
    private pendingRequests: Map<string, PendingRequest> = new Map();
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private heartbeatInterval?: NodeJS.Timeout;
    private apiBaseUrl: string;
    private token: string;

    constructor() {
        this.apiBaseUrl = API_BASE_URL;
        this.token = "NOT-SET";
    }

    private generateSessionId(user: any): string {
        this.token = user.signInUserSession.idToken.jwtToken;

        return 'sess-' + Math.random().toString(36).substring(2, 15) +
               Math.random().toString(36).substring(2, 15);
    }

    private generateRequestId(): string {
        return 'req-' + Math.random().toString(36).substring(2, 15) +
               Math.random().toString(36).substring(2, 15);
    }

    private generateConversationId(): string {
        return 'conv-' + Math.random().toString(36).substring(2, 15) +
               Math.random().toString(36).substring(2, 15);
    }

    getOrCreateSession(user: any, inspectionId?: string): { sessionId: string; isNew: boolean } {
        this.token = user.signInUserSession.idToken.jwtToken;
        const storageKey = 'idwal-chat-session';
        const stored = localStorage.getItem(storageKey);
        
        if (stored) {
            const session = JSON.parse(stored);
            const age = Date.now() - session.timestamp;
            
            // Reuse if less than 30 minutes old and matches current inspection
            if (age < 1800000 && session.inspectionId === inspectionId) {
                return { sessionId: session.sessionId, isNew: false };
            }
        }
        
        // Create new session
        const sessionId = this.generateSessionId(user);
        localStorage.setItem(storageKey, JSON.stringify({
            sessionId,
            inspectionId,
            timestamp: Date.now()
        }));
        
        return { sessionId, isNew: true };
    }

    refreshSession(sessionId: string): void {
        const storageKey = 'idwal-chat-session';
        const stored = localStorage.getItem(storageKey);
        const session = stored ? JSON.parse(stored) : {};
        
        localStorage.setItem(storageKey, JSON.stringify({
            sessionId,
            inspectionId: session.inspectionId,
            timestamp: Date.now()
        }));
    }

    async createSession(user: any, inspectionId?: string): Promise<ChatSession> {
        console.log('Creating a new session for inspection', inspectionId);

        const { sessionId, isNew } = this.getOrCreateSession(user, inspectionId);
        const conversationId = this.generateConversationId();

        // Call backend to create session if it's a new session
        if (isNew) {
            const backendSessionId = await this.createSessionOnBackend(inspectionId);
            
            // Update localStorage with backend session ID
            const storageKey = 'idwal-chat-session';
            localStorage.setItem(storageKey, JSON.stringify({
                sessionId: backendSessionId,
                inspectionId,
                timestamp: Date.now()
            }));
            
            // Start heartbeat with backend session ID
            this.startHeartbeat(backendSessionId, conversationId);

            // Check for pending requests from previous session
            await this.checkPendingRequests(backendSessionId);

            return {
                sessionId: backendSessionId,
                conversationId,
                createdAt: new Date().toISOString()
            };
        }

        // Start heartbeat
        this.startHeartbeat(sessionId, conversationId);

        // Check for pending requests from previous session
        await this.checkPendingRequests(sessionId);

        return {
            sessionId,
            conversationId,
            createdAt: new Date().toISOString()
        };
    }

    private async createSessionOnBackend(inspectionId?: string): Promise<string> {
        try {
            const response = await fetch(`${this.apiBaseUrl}/Inspection/Chat/Session`, {
                method: 'POST',
                headers: {
                    "Authorization": "Bearer " + this.token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    inspectionId
                })
            });

            if (response.status === 503) {
                throw new Error('SERVICE_UNAVAILABLE');
            }

            if (!response.ok) {
                throw new Error(`Failed to create session: HTTP ${response.status}`);
            }

            const data = await response.json();
            return data.sessionId;
        } catch (error: any) {
            if (error.message === 'SERVICE_UNAVAILABLE') {
                throw error;
            }
            console.error('Failed to create session on backend:', error);
            throw error;
        }
    }

    private startHeartbeat(sessionId: string, conversationId: string): void {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }

        this.heartbeatInterval = setInterval(async () => {
            try {
                console.log('Heartbeat:', sessionId, conversationId);
                this.refreshSession(sessionId);
            } catch (error) {
                console.error('Heartbeat failed:', error);
            }
        }, 60000);
    }

    private async checkPendingRequests(sessionId: string): Promise<void> {
        const pendingKey = `pending-requests-${sessionId}`;
        const pending = localStorage.getItem(pendingKey);
        
        if (pending) {
            const requests = JSON.parse(pending);
            
            for (const requestId of requests) {
                await this.checkRequestStatus(requestId);
            }
            
            localStorage.removeItem(pendingKey);
        }
    }

    private async checkRequestStatus(requestId: string): Promise<void> {
        try {
            console.log('Checking status for request:', requestId);
        } catch (error) {
            console.error('Failed to check request status:', error);
        }
    }

    private addPendingRequest(sessionId: string, requestId: string): void {
        const key = `pending-requests-${sessionId}`;
        const pending = JSON.parse(localStorage.getItem(key) || '[]');
        pending.push(requestId);
        localStorage.setItem(key, JSON.stringify(pending));
    }

    private removePendingRequest(sessionId: string, requestId: string): void {
        const key = `pending-requests-${sessionId}`;
        const pending = JSON.parse(localStorage.getItem(key) || '[]');
        const filtered = pending.filter((id: string) => id !== requestId);
        localStorage.setItem(key, JSON.stringify(filtered));
    }

    private isRetryable(error: any): boolean {
        // Network errors are retryable
        if (error.name === 'TypeError' || error.name === 'NetworkError') {
            return true;
        }
        
        // Timeout errors are retryable
        if (error.name === 'AbortError' || error.message?.includes('timeout')) {
            return true;
        }
        
        // 5xx errors are retryable
        if (error.message?.match(/HTTP 5\d\d/)) {
            return true;
        }
        
        // 429 (rate limit) is retryable
        if (error.message?.includes('HTTP 429')) {
            return true;
        }
        
        return false;
    }

    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async sendChat(sessionId: string, message: string, inspectionId?: string, retryCount = 0): Promise<SendChatResponse> {
        const requestId = this.generateRequestId();
        const maxRetries = 3;

        try {
            // Store request state
            this.pendingRequests.set(requestId, {
                message,
                status: 'sending',
                timestamp: Date.now(),
                retryCount
            });

            // Track as pending locally
            this.addPendingRequest(sessionId, requestId);

            console.log('Sending chat message:', sessionId, message, requestId);
            
            // Real API call
            const response = await fetch(`${this.apiBaseUrl}/Inspection/Chat`, {
                method: 'POST',
                headers: {
                    "Authorization": "Bearer " + this.token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    message,
                    sessionId,
                    conversationId: null, // Will be managed by backend
                    inspectionId
                })
            });

            if (response.status === 503) {
                throw new Error('SERVICE_UNAVAILABLE');
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();
            console.log('Response received:', data);

            // Update request status
            const request = this.pendingRequests.get(requestId);
            if (request) {
                request.status = 'completed';
            }

            // Remove from pending
            this.removePendingRequest(sessionId, requestId);
            this.pendingRequests.delete(requestId);

            // Format response
            const userMessage: ChatMessage = {
                id: `msg_${Date.now()}`,
                text: message,
                isUser: true,
                timestamp: new Date().toISOString(),
                requestId
            };

            const aiResponse: ChatMessage = {
                id: `msg_${Date.now() + 1}`,
                text: data.response || "I'm here to help! How can I assist you with your report insights?",
                isUser: false,
                timestamp: new Date().toISOString(),
                requestId
            };

            return {
                message: userMessage,
                aiResponse
            };

        } catch (error: any) {
            console.error('Send message error:', error);
            
            // Determine if retryable
            if (this.isRetryable(error) && retryCount < maxRetries) {
                const backoffMs = Math.min(1000 * Math.pow(2, retryCount), 10000);
                await this.delay(backoffMs);
                return this.sendChat(sessionId, message, inspectionId, retryCount + 1);
            }
            
            // Non-retryable or max retries exceeded
            const request = this.pendingRequests.get(requestId);
            if (request) {
                request.status = 'failed';
            }
            this.removePendingRequest(sessionId, requestId);
            throw error;
        }
    }

    cleanup(): void {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }
        this.pendingRequests.clear();
    }
}

export const askIdwalAIService = new AskIdwalAIService();

// Setup cleanup on page unload
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        askIdwalAIService.cleanup();
    });
}
